import {quoteWorkspaceEnrichmentApi} from '../../api/workspaceApi';
import {buildActionCreditEstimate, isCreditBalanceResolved} from '../../helper/actionCreditEstimate';
import {errorDetail} from '../../helper/errorDetail';

/** `/batch/submit` refuses more than 100 company_ids; quoted runs get the same refusal from the backend. */
export const AI_BATCH_MAX_COMPANIES = 100;

/**
 * Saved Search page grid context. A stored snapshot attaches the run to the saved search;
 * a live re-run is view-only. Owner ruling 2026-09-30: a single-row run keeps its
 * price-on-button and skips the confirmation.
 */
export const savedSearchGridContext = ({savedSearchId, showingSnapshot}) => ({
  workspaceId: showingSnapshot ? savedSearchId : null,
  isDirty: false,
  reviewSingleRow: false,
});

const quoteError = (response) => {
  if (response?.status === 422) {
    return 'These companies are outside the saved search snapshot, or the model is unavailable. Save the current search before charging.';
  }
  if (response?.status === 404) return 'This saved search is no longer available. Save the current search before charging.';
  return errorDetail(response?.data?.detail, 'Unable to load a quote. No credits were charged.');
};

/** Backend quote for a frozen saved-search scope; `{quote}` only when it is bound to exactly that scope and model. */
export async function fetchWorkspaceQuote({workspaceId, companyIds, provider, model}) {
  try {
    const response = await quoteWorkspaceEnrichmentApi(workspaceId, {company_ids: companyIds, llm_provider: provider, model});
    const quote = response?.data;
    const bound = response?.status === 200 && quote?.quote_token && Number.isFinite(quote.max_credits)
      && quote.max_credits >= 0 && Number.isFinite(quote.credits_per_company)
      && quote.company_count === companyIds.length && quote.provider === provider && quote.model === model;
    return bound ? {quote} : {error: quoteError(response)};
  } catch {
    return {error: quoteError(null)};
  }
}

/** Owner ruling 2026-09-30: confirm-before-spend only for runs covering more than one row. */
export const needsPaidRunReview = (companyCount, searchContext) =>
  companyCount > 1 || Boolean(searchContext && searchContext.reviewSingleRow !== false);

/** The price a grid confirmation shows: the backend quote when one exists, else the client upper bound. */
export function priceGridRun({review, quote, credits}) {
  const count = review.companyIds.length;
  const quoted = Boolean(review.workspaceId);
  const estimate = buildActionCreditEstimate({
    actionId: review.actionId || 'ai_enrichment', selectedCount: review.unitCount ?? count, aiModelId: review.model,
  });
  const total = quoted ? (quote?.max_credits ?? null) : estimate.maxCreditsUsed;
  const available = credits?.balance?.totalAvailable ?? null;
  // Without a balance provider (isolated renders) the backend 402 stays the only balance check.
  const balanceResolved = !credits?.providerPresent || isCreditBalanceResolved({
    availableCredits: available, loading: credits.loading, refreshing: credits.refreshing, error: credits.error,
  });
  const shortfall = credits?.providerPresent && balanceResolved && total !== null ? Math.max(0, total - available) : 0;
  const overLimit = !quoted && review.kind !== 'data' && count > AI_BATCH_MAX_COMPANIES;
  return {
    total,
    perUnit: quoted ? quote?.credits_per_company : estimate.creditsPerUnit,
    available,
    shortfall,
    balanceResolved,
    overLimit,
    blocked: overLimit || !balanceResolved || shortfall > 0,
  };
}
